// utils/workos.ts
export interface WorkOSSession {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    emailVerified: boolean;
    profilePictureUrl?: string | null;
  };
  accessToken: string;
  role?: string;
  roles?: string[];
  authProvider: 'cognito' | 'workos';
}

const API_URL =
  import.meta.env.VITE_AUTH_BASE_URL ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:8000'
    : 'https://api.workos.yuso.io');

const LOGIN_URL =
  import.meta.env.VITE_LOGIN ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:3000'
    : 'https://dev.login.yuso.com');

export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(
      atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
    );
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
};

const decodeToken = (token: string): any => {
  try {
    return JSON.parse(
      atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
    );
  } catch {
    return null;
  }
};

const getCookie = (name: string): string | null => {
  const prefix = `${name}=`;
  const match = document.cookie
    .split('; ')
    .find((r) => r.startsWith(prefix));

  if (!match) return null;

  // Use indexOf instead of split('=') to avoid breaking on '=' in JWT/base64
  const raw = match.substring(prefix.length);

  try {
    const parsed = JSON.parse(raw);
    return parsed?.AccessToken || null;
  } catch {
    return null;
  }
};

/** Returns the raw WorkOS access token from the cookie, or null if absent/expired. */
export const getWorkOSAccessToken = (): string | null => {
  const token = getCookie('workos_session');
  return token && !isTokenExpired(token) ? token : null;
};

export const getWorkOSSession = async (): Promise<WorkOSSession | null> => {
  // ── 1. Try WorkOS token ──────────────────────────────────────────────────
  const workosToken = getCookie('workos_session');
  if (workosToken && !isTokenExpired(workosToken)) {
    const p = decodeToken(workosToken);

    if (p) {
      try {
        const res = await fetch(`${API_URL}/api/v1/auth/user/client-info`, {
          credentials: 'include',
          headers: { Authorization: `Bearer ${workosToken}` },
        });

        if (res.ok) {
          const body = await res.json();
          const u = Array.isArray(body.client) ? body.client[0] : body.client || body;

          if (u) {
            return {
              user: {
                id: u.id?.toString() || p.sub || '',
                email: u.email || '',
                firstName: u.firstName || u.first_name || '',
                lastName: u.lastName || u.last_name || '',
                emailVerified: true,
              },
              accessToken: workosToken,
              role: p.role,
              roles: Array.isArray(p.roles) ? p.roles : [],
              authProvider: 'workos',
            };
          }
        }
      } catch (err) {
        console.error('Error fetching WorkOS user info:', err);
      }

      // Fallback — return what we can from token alone
      return {
        user: {
          id: p.sub || '',
          email: '',
          firstName: '',
          lastName: '',
          emailVerified: true,
        },
        accessToken: workosToken,
        role: p.role,
        roles: Array.isArray(p.roles) ? p.roles : [],
        authProvider: 'workos',
      };
    }
  }

  // ── 2. Try Cognito token ─────────────────────────────────────────────────
  const cognitoToken = getCookie('session');
  if (cognitoToken && !isTokenExpired(cognitoToken)) {
    try {
      const res = await fetch(`${API_URL}/api/v1/auth/user/client-info`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${cognitoToken}` },
      });

      if (!res.ok) {
        console.error('Cognito client-info failed:', res.status);
        return null;
      }

      const body = await res.json();
      const u = Array.isArray(body.client) ? body.client[0] : body.client || body;
      if (!u) return null;

      return {
        user: {
          id: u.id?.toString() || u.sub || '',
          email: u.email || '',
          firstName: u.firstName || u.first_name || '',
          lastName: u.lastName || u.last_name || '',
          emailVerified: true,
        },
        accessToken: cognitoToken,
        role: u.role,
        roles: u.roles
          ? Array.isArray(u.roles)
            ? u.roles
            : [u.roles]
          : [],
        authProvider: 'cognito',
      };
    } catch (err) {
      console.error('Error fetching Cognito session:', err);
      return null;
    }
  }

  // ── 3. No valid token — redirect to login ────────────────────────────────
  // returnUrl brings the user back to this exact page (e.g. a contract/document permalink) after login
  const loginUrl = new URL(LOGIN_URL);
  loginUrl.searchParams.set('returnUrl', window.location.href);
  window.location.href = loginUrl.toString();
  return null;
};

export const logout = () => {
  const isLocalhost = window.location.hostname === 'localhost';

  if (isLocalhost) {
    // On HTTP localhost SameSite=None;Secure won't work — omit domain and use Lax
    document.cookie = `workos_session=; path=/; SameSite=Lax; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = `session=; path=/; SameSite=Lax; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  } else {
    for (const domain of ['.yuso.com', '.yuso.io']) {
      const opts = `path=/; domain=${domain}; SameSite=None; Secure; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      document.cookie = `workos_session=; ${opts}`;
      document.cookie = `session=; ${opts}`;
    }
  }

  window.location.href = LOGIN_URL;
};

export const getUserId = (s: WorkOSSession | null) => s?.user?.id;
export const getAccessToken = (s: WorkOSSession | null) => s?.accessToken;
export const hasRole = (s: WorkOSSession | null, role: string): boolean => {
  if (!s) return false;
  return s.role === role || (Array.isArray(s.roles) && s.roles.includes(role));
};

